<script setup lang="ts">
import { watch, onMounted } from "vue";

type tabModel = {
  id: string | number;
  title: string;
  slotName?: string;
};

const props = defineProps<{
  tabs: tabModel[];
  modelValue?: string | number;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | undefined): void;
  (e: "tab-change", tab: tabModel): void;
}>();

// Устанавливаем первый таб по умолчанию
onMounted(() => {
  if (props.modelValue === undefined && props.tabs.length > 0) {
    emit("update:modelValue", props.tabs[0].id);
  }
});

// Следим за сменой активного таба
watch(
  () => props.modelValue,
  (newTabId) => {
    if (newTabId !== undefined) {
      const activeTab = props.tabs.find((tab) => tab.id === newTabId);
      if (activeTab) {
        emit("tab-change", activeTab);
      }
    }
  },
);

// Если tabs меняются
watch(
  () => props.tabs,
  (newTabs) => {
    if (props.modelValue === undefined && newTabs.length > 0) {
      emit("update:modelValue", newTabs[0].id);
    }
  },
  { immediate: true },
);

const setActiveTab = (tabId: string | number) => {
  emit("update:modelValue", tabId);
};
</script>

<template>
  <div class="app-tabs">
    <div class="app-tabs__header">
      <div
        v-for="tab in tabs"
        :key="tab.id"
        :class="['app-tabs__header-button', { active: modelValue === tab.id }]"
        @click="setActiveTab(tab.id)"
      >
        {{ tab.title }}
      </div>
      <slot name="additionalContent" />
    </div>

    <div class="app-tabs__content">
      <div
        class="app-tabs__content-tab"
        v-for="tab in tabs"
        :key="tab.id"
        v-show="modelValue === tab.id"
      >
        <slot :name="tab.slotName" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/styles/color.scss";

.app-tabs {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;

  &__header {
    margin: 0 12px;
    height: 48px;
    min-height: 48px;
    flex: 0 0 48px;
    display: flex;
    align-items: center;
    gap: 24px;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    color: $color-text-secondary;
    border-bottom: 1px solid $color-divider;
    box-sizing: border-box;

    &-button {
      height: 100%;
      display: flex;
      align-items: center;
      flex: 0 0 auto;
      white-space: nowrap;
      padding: 0;
      border-bottom: 2px solid transparent;
      box-sizing: border-box;

      &:hover {
        cursor: pointer;
        color: $color-accent;
      }

      &:active {
        border-bottom: 2px solid $color-accent;
      }
    }

    &-button.active {
      color: $color-accent;
      border-bottom: 2px solid $color-accent;
    }

    &-button.disabled {
      cursor: default;
      color: $color-divider;
    }
  }

  &__content {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    &-tab {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  }
}
</style>
